import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  getInvitationAPI,
  acceptInvitationAPI,
  getMeAPI,
  type InvitationInfo,
} from '../api/auth';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import Logo from '../components/ui/Logo';

const TOKEN_KEY = 'ellezeta_token';

export default function AccettaInvitoPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'error' | 'ready'>('loading');
  // Lo stato contiene la CHIAVE i18n (render con t; il confronto a render usa la chiave).
  const [errorMsg, setErrorMsg] = useState('auth.invito.link_non_valido');

  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      if (!token) {
        setLoadState('error');
        setErrorMsg('auth.invito.link_non_valido');
        return;
      }
      try {
        const data = await getInvitationAPI(token);
        setInvitation(data);
        setLoadState('ready');
      } catch (err: unknown) {
        setLoadState('error');
        if (axios.isAxiosError(err)) {
          const detail = err.response?.data?.detail ?? '';
          if (detail.toLowerCase().includes('scaduto')) {
            setErrorMsg('auth.invito.scaduto');
          } else if (detail.toLowerCase().includes('già utilizzato')) { // i18n-ignore: match su messaggio BE
            setErrorMsg('auth.invito.gia_utilizzato');
          } else if (detail.toLowerCase().includes('revocato')) {
            setErrorMsg('auth.invito.revocato');
          } else {
            setErrorMsg('auth.invito.link_non_valido');
          }
        } else {
          setErrorMsg('auth.invito.link_non_valido');
        }
      }
    })();
  }, [token]);

  function validateForm(): string[] {
    const errors: string[] = [];
    if (!nome.trim()) errors.push(t('auth.invito.nome_obbligatorio'));
    if (!cognome.trim()) errors.push(t('auth.invito.cognome_obbligatorio'));
    if (password.length < 8) errors.push(t('auth.validazione.password_min'));
    if (!/[A-Z]/.test(password)) errors.push(t('auth.validazione.password_maiuscola'));
    if (!/[a-z]/.test(password)) errors.push(t('auth.validazione.password_minuscola'));
    if (!/[0-9]/.test(password)) errors.push(t('auth.validazione.password_numero'));
    if (password !== confirmPassword) errors.push(t('auth.validazione.password_non_corrispondono'));
    return errors;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errors = validateForm();
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors([]);
    setSubmitting(true);
    setSubmitError(null);

    try {
      const result = await acceptInvitationAPI({ token, nome, cognome, password });

      localStorage.setItem(TOKEN_KEY, result.access_token);
      useAuthStore.setState({ token: result.access_token });

      const user = await getMeAPI();
      useAuthStore.getState().setUser(user, result.access_token);

      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail ?? '';
        if (status === 404) {
          setSubmitError(t('auth.invito.non_piu_valido', { detail }));
        } else if (status === 409 && detail) {
          // «Email già registrata come utente»: il motivo lo sa solo il backend
          // (l'invito è ancora valido, è l'utente che esiste già). Nel ramo
          // generico diventava «Riprova», e riprovare non funzionerà mai.
          setSubmitError(detail);
        } else if (status === 422) {
          const errors422 = err.response?.data?.detail;
          if (Array.isArray(errors422)) {
            setFormErrors(errors422.map((e: { msg: string }) => e.msg));
          } else {
            setSubmitError(detail || t('auth.invito.errore_validazione'));
          }
        } else {
          setSubmitError(t('auth.invito.errore_registrazione'));
        }
      } else {
        setSubmitError(t('auth.invito.errore_registrazione'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-stone-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-md border border-stone-mid overflow-hidden">
          <div className="flex justify-center py-6 border-b border-stone-mid px-6">
            <Logo width={140} />
          </div>

          {loadState === 'loading' && (
            <div className="flex items-center justify-center py-12 text-ink-soft text-sm">
              <svg className="animate-spin mr-2 h-5 w-5" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('auth.invito.verifica_in_corso')}
            </div>
          )}

          {loadState === 'error' && (
            <div className="px-6 py-8 text-center">
              <p className="text-lg font-semibold text-ink-dark mb-2">{t(errorMsg)}</p>
              <p className="text-sm text-ink-soft mb-6">
                {errorMsg === 'auth.invito.gia_utilizzato'
                  ? t('auth.invito.gia_utilizzato_testo')
                  : t('auth.invito.link_non_valido_testo')}
              </p>
              <a
                href="/login"
                className="inline-block px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors"
              >
                {t('auth.invito.vai_al_login')}
              </a>
            </div>
          )}

          {loadState === 'ready' && invitation && (
            <div className="px-6 py-6">
              <h1 className="text-lg font-semibold text-ink-dark mb-1">
                {t('auth.invito.completa_titolo')}
              </h1>
              <p className="text-sm text-ink-soft mb-5">
                {t('auth.invito.invitato_come')}{' '}
                <span className="font-medium text-ink-dark">{invitation.role_label}</span> {t('auth.invito.da')}{' '}
                {invitation.invited_by_name}.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink-dark mb-1">{t('auth.login.label_email')}</label>
                  <input
                    type="email"
                    value={invitation.email}
                    readOnly
                    className="w-full px-3 py-2 text-sm border border-stone-border rounded-lg bg-stone-bg text-ink-soft cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-dark mb-1">
                    {t('auth.invito.label_nome')} <span className="text-accent-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    required
                    className="w-full px-3 py-2 text-sm border border-stone-border rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-dark mb-1">
                    {t('auth.invito.label_cognome')} <span className="text-accent-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={cognome}
                    onChange={(e) => setCognome(e.target.value)}
                    required
                    className="w-full px-3 py-2 text-sm border border-stone-border rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-dark mb-1">
                    {t('auth.login.label_password')} <span className="text-accent-danger">*</span>
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 text-sm border border-stone-border rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage"
                  />
                  <p className="mt-1 text-xs text-ink-soft">
                    {t('auth.reset.requisiti_password')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-dark mb-1">
                    {t('auth.reset.conferma_password')} <span className="text-accent-danger">*</span>
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 text-sm border border-stone-border rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage"
                  />
                </div>

                {formErrors.length > 0 && (
                  <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger space-y-1">
                    {formErrors.map((err, i) => (
                      <p key={i}>{err}</p>
                    ))}
                  </div>
                )}

                {submitError && (
                  <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2.5 px-4 text-sm font-semibold text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? t('auth.invito.creazione_in_corso') : t('auth.invito.crea_account')}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
